/**
 * 📡 Admin Stations - Cetalingua FM
 * * Monitoring Dashboard
 * - Lists all stations
 * - Aggregates stats (Uploads, Storage, Listeners)
 * - Sortable by activity/usage
 */

import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase-config';
import './AdminStations.css'; // Uses existing or shared admin styles

const AdminStations = () => {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('totalUploads'); // Default sort
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    loadStationsData();
  }, []);

  const loadStationsData = async () => {
    setLoading(true);
    try {
      // 1. Get all stations
      const q = query(collection(db, 'stations'), orderBy('stationId', 'asc'));
      const snapshot = await getDocs(q);
      
      // 2. For each station, fetch its stats (Files & Listeners)
      // Note: In a production app with 1000s of stations, these stats should be 
      // counters on the parent document to avoid N+1 reads.
      const stationPromises = snapshot.docs.map(async (doc) => {
        const data = doc.data();
        const uid = doc.id;

        // A. Get File Counts & Storage
        // We use getDocs here to sum the size, if you just wanted count, getCountFromServer is cheaper
        const filesRef = collection(db, 'stations', uid, 'files');
        const filesSnap = await getDocs(filesRef);
        let totalBytes = 0;
        filesSnap.forEach(f => totalBytes += (f.data().size || 0));
        
        // Find latest upload date
        let lastActive = data.updatedAt || null; // Default to profile update
        if (!filesSnap.empty) {
            const lastFile = filesSnap.docs[filesSnap.docs.length - 1].data();
            if (lastFile.uploadedAt) lastActive = lastFile.uploadedAt;
        }

        // B. Get Listener Stats
        const listenersRef = collection(db, 'stations', uid, 'listeners');
        const listenersSnap = await getDocs(listenersRef);
        let totalMinutes = 0;
        listenersSnap.forEach(l => totalMinutes += (l.data().minutesListened || 0));

        return {
          uid,
          ...data,
          totalUploads: filesSnap.size,
          totalBytes: totalBytes,
          totalListeners: listenersSnap.size,
          totalHoursListened: totalMinutes / 60,
          lastActive: lastActive,
          // ✅ PATCH: Add pipeline processing fields
          processingStatus: data.processingStatus || 'unknown',
          chunkCount: data.chunkCount || 0,
          signalCount: data.signalCount || 0,
          siameseStatus: data.siameseStatus || '',
          umapClusters: data.umapClusters || 0,
          lastPipelineRun: data.preprocessedAt || data.detectedAt || data.completedAt || null,
        };
      });

      const fullData = await Promise.all(stationPromises);
      
      // Initial sort
      const sortedData = sortData(fullData, 'totalUploads', 'desc');
      setStations(sortedData);

    } catch (error) {
      console.error('Error loading station stats:', error);
      alert('Failed to load station data');
    } finally {
      setLoading(false);
    }
  };

  const sortData = (data, field, direction) => {
    return [...data].sort((a, b) => {
      let valA = a[field] || 0;
      let valB = b[field] || 0;

      // Handle dates
      if (field === 'lastActive') {
        valA = new Date(valA || 0).getTime();
        valB = new Date(valB || 0).getTime();
      }

      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (field) => {
    const newDirection = field === sortField && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortDirection(newDirection);
    setStations(sortData(stations, field, newDirection));
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Never';
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short', 
      day: 'numeric', 
      year: 'numeric'
    });
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '⬆️' : '⬇️';
  };

  const getPipelineStatusStyle = (status) => {
    if (status === 'complete') {
      return {
        backgroundColor: '#10B98122',
        color: '#10B981'
      };
    } else if (status?.includes('error')) {
      return {
        backgroundColor: '#EF444422',
        color: '#EF4444'
      };
    } else {
      return {
        backgroundColor: '#FFA72F22',
        color: '#FFA72F'
      };
    }
  };

  return (
    <div className="admin-stations">
      <div className="detections-header">
        <h2>📡 Station Summary</h2>
        <p>Real-time usage and listener statistics</p>
        <button className="refresh-btn" onClick={loadStationsData} style={{ marginTop: '10px' }}>
          🔄 Refresh Stats
        </button>
      </div>

      {loading ? (
        <div className="loading-state"><p>Aggregating station data...</p></div>
      ) : stations.length === 0 ? (
        <div className="empty-state">
          <h3>No Stations Found</h3>
          <p>Users haven't created any stations yet.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('stationId')} style={{ cursor: 'pointer' }}>
                  ID {getSortIcon('stationId')}
                </th>
                <th>Station Name</th>
                <th onClick={() => handleSort('totalUploads')} style={{ cursor: 'pointer' }}>
                  Uploads {getSortIcon('totalUploads')}
                </th>
                <th onClick={() => handleSort('totalBytes')} style={{ cursor: 'pointer' }}>
                  Storage {getSortIcon('totalBytes')}
                </th>
                <th onClick={() => handleSort('totalListeners')} style={{ cursor: 'pointer' }}>
                  Listeners {getSortIcon('totalListeners')}
                </th>
                <th onClick={() => handleSort('totalHoursListened')} style={{ cursor: 'pointer' }}>
                  Hours {getSortIcon('totalHoursListened')}
                </th>
                <th onClick={() => handleSort('lastActive')} style={{ cursor: 'pointer' }}>
                  Last Active {getSortIcon('lastActive')}
                </th>
                {/* ✅ PATCH: Add new column headers */}
                <th>Pipeline</th>
                <th>Chunks</th>
                <th>Signals</th>
                <th>Clusters</th>
              </tr>
            </thead>
            <tbody>
              {stations.map(station => (
                <tr key={station.uid}>
                  <td>
                    <span className="station-id-badge">#{station.stationId || '---'}</span>
                  </td>
                  <td className="company-cell">
                    <strong>{station.name || 'Unnamed Station'}</strong>
                    <div style={{ fontSize: '11px', color: '#8899AA' }}>
                      {station.city ? `${station.city}, ${station.country}` : 'No Location'}
                    </div>
                  </td>
                  <td>
                    <span style={{ fontWeight: 600 }}>{station.totalUploads}</span> files
                  </td>
                  <td>
                    {formatBytes(station.totalBytes)}
                  </td>
                  <td>
                    {station.totalListeners}
                  </td>
                  <td>
                    {Math.round(station.totalHoursListened * 10) / 10} hrs
                  </td>
                  <td style={{ color: station.lastActive ? 'inherit' : '#8899AA' }}>
                    {formatDate(station.lastActive)}
                  </td>
                  {/* ✅ PATCH: Add new pipeline status cell */}
                  <td>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      ...getPipelineStatusStyle(station.processingStatus),
                    }}>
                      {station.processingStatus || '—'}
                    </span>
                  </td>
                  {/* ✅ PATCH: Add new data cells */}
                  <td>{station.chunkCount || '—'}</td>
                  <td>{station.signalCount || '—'}</td>
                  <td>{station.umapClusters || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminStations;